import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { TrendingUp, PlayCircle, X, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL;

export default function FilmScoreHistory({ psi }) {
  const [analyses, setAnalyses] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [shared, setShared] = useState({});

  const share = async (a) => {
    setShared((s) => ({ ...s, [a.id]: "sharing" }));
    try {
      await api.post("/community/posts", {
        content: `My AI film breakdown — ${a.score}/100 🎬 Graded by the Bullpen scout.`,
        video: a.video_url, type: "highlight",
      });
      setShared((s) => ({ ...s, [a.id]: "done" }));
      toast.success("Shared to the Community feed!");
    } catch (e) {
      setShared((s) => ({ ...s, [a.id]: null }));
      toast.error(e.response?.data?.detail || "Could not share clip");
    }
  };

  useEffect(() => {
    api.get("/videos/analyses", { params: { kind: "gameplay" } })
      .then(({ data }) => setAnalyses(data.filter((a) => a.status === "complete" && a.score != null).reverse()))
      .catch(() => {});
  }, []);

  if (analyses.length === 0)
    return <div className="text-sm text-zinc-500" data-testid="film-score-empty">No film scores yet — upload a game video in the LDA Training Hub to set your baseline.</div>;

  const chartData = analyses.map((a, i) => ({
    n: i + 1,
    score: a.score,
    date: a.created_at ? new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : `#${i + 1}`,
  }));
  const latest = analyses[analyses.length - 1].score;
  const first = analyses[0].score;
  const delta = latest - first;

  return (
    <div data-testid="film-score-history">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        {analyses.length > 1 && (
          <span data-testid="film-score-delta" className={`flex items-center gap-1 text-xs font-bold font-mono ${delta >= 0 ? "text-pb-red" : "text-red-400"}`}>
            <TrendingUp className={`w-3.5 h-3.5 ${delta < 0 ? "rotate-180" : ""}`} /> {delta >= 0 ? "+" : ""}{delta} since first film
          </span>
        )}
        <span className="ml-auto text-xs text-zinc-500 font-mono">Latest: <span className="text-pb-cyan font-bold">{latest}/100</span></span>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Every AI game-film score, in order. Your average feeds your Player Skill Index.</p>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fill: "#52525B", fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #27272A", color: "#fff" }}
            labelStyle={{ color: "#A1A1AA" }} formatter={(v) => [`${v}/100`, "Film score"]} />
          {psi != null && psi > 0 && (
            <ReferenceLine y={psi} stroke="#22D3EE" strokeDasharray="4 4"
              label={{ value: `PSI ${psi}`, fill: "#22D3EE", fontSize: 11, position: "insideTopRight" }} />
          )}
          <Line type="monotone" dataKey="score" stroke="#A3E635" strokeWidth={2}
            dot={{ fill: "#A3E635", r: 4 }} activeDot={{ r: 6, fill: "#BEF264" }} />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-4 space-y-2" data-testid="film-vault">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Film Vault — recent analyses</div>
        {analyses.slice(-5).reverse().map((a) => (
          <div key={a.id} className="border border-pb-border/60 bg-pb-elevated/30">
            <div className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="font-display font-black text-pb-cyan w-14">{a.score}/100</span>
              <span className="flex-1 min-w-0 truncate text-zinc-400">
                {a.created_at ? new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""} · Game film
              </span>
              {a.video_url ? (
                <span className="flex items-center gap-3 shrink-0">
                  <button data-testid={`film-share-${a.id}`} onClick={() => share(a)} disabled={!!shared[a.id]}
                    className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#F472B6] hover:text-[#BEF264] transition-colors disabled:opacity-60">
                    {shared[a.id] === "sharing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                    {shared[a.id] === "done" ? "Shared ✓" : "Share"}
                  </button>
                  <button data-testid={`film-watch-${a.id}`} onClick={() => setPlaying(playing === a.id ? null : a.id)}
                    className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-pb-cyan hover:text-[#BEF264] transition-colors">
                    {playing === a.id ? <><X className="w-3.5 h-3.5" /> Close</> : <><PlayCircle className="w-3.5 h-3.5" /> Watch</>}
                  </button>
                </span>
              ) : (
                <span className="text-[9px] uppercase tracking-widest text-zinc-600">no replay</span>
              )}
            </div>
            {playing === a.id && a.video_url && (
              <video src={MEDIA_BASE + a.video_url} controls playsInline autoPlay
                className="w-full max-h-80 bg-black" data-testid={`film-player-${a.id}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
